import React, { useEffect, useMemo, useState } from 'react';
import {
  Award,
  CalendarPlus,
  CheckCircle2,
  ClipboardList,
  Plus,
  Trophy,
  Users,
} from 'lucide-react';
import scholarshipService from '../../services/scholarship.service';
import {
  ScholarshipCampaign,
  ScholarshipCreatePayload,
  ScholarshipRegistration,
} from '../../types/scholarship';

const DEFAULT_TELUGU_DESCRIPTION = 'విద్యార్థుల విద్యాభివృద్ధి కోసం అంకితభావంతో కృషి చేసిన\nశ్రీ ఎన్. సూర్యనారాయణ గారి స్మారకార్థం ఈ స్కాలర్‌షిప్ కార్యక్రమాన్ని నిర్వహిస్తున్నాము.\n\nప్రతిభావంతులైన విద్యార్థులను ప్రోత్సహించడం, వారికి విద్యా అవకాశాలను అందించడం మరియు ఆయన విద్యాసేవా వారసత్వాన్ని కొనసాగించడం ఈ కార్యక్రమం ముఖ్య ఉద్దేశ్యం.';
const defaultCampaignForm = (): ScholarshipCreatePayload => {
  return {
    title: 'N. Surya Narayana Memorial Scholarship 2026',
    description:
      'The N. Surya Narayana Memorial Scholarship has been established in loving memory of Late Sri N. Surya Narayana Garu, a passionate teacher who dedicated his life to nurturing knowledge, values, and education among students.',
    tagline: 'Honoring a Passionate Teacher. Empowering Future Learners.',
    heroSubtitle: 'A Tribute to a Passionate Teacher. A Step Towards a Brighter Future.',
    heroFeatures: [
      'Free Registration',
      'Scholarship Examination',
      'Merit-Based Selection',
      'Interview Round',
      'Scholarship Awards',
    ],
    teluguDescription: DEFAULT_TELUGU_DESCRIPTION,
    memorialName: 'Late Sri N. Surya Narayana Garu',
    sponsorName: 'LocalAlly',
    purpose:
      'This scholarship aims to recognize talented and deserving students, encourage academic excellence, and continue his legacy of supporting education.',
    eligibility: 'Class 10 students only.',
    instructions: 'Registrations have been extended until 25 July 2026. Register for free as a Class 10 student. Enter the student\'s first name and last name(s) exactly as printed on the Aadhaar card. The scholarship exam is scheduled for 10:00 AM on Sunday, 26 July 2026 and has 30 questions for 30 marks, 30 minutes, and an 80% passing requirement. Shortlisted students must carry their Aadhaar card for verification at the interview.',
    recognitionHighlights: [
      'Top 10 Students: Cash Prize + Merit Certificate',
      'Top 20 Students: Merit Certificate',
      'All Participants: Digital Participation Certificate',
    ],
    participationCertificateText:
      'This certificate is proudly presented to [Student Name] for participating in the N. Surya Narayana Memorial Scholarship Examination 2026 conducted by LocalAlly in honor of a dedicated teacher whose passion for education continues to inspire future generations.',
    meritCertificateText:
      'This certificate is awarded to [Student Name] for securing a distinguished position in the N. Surya Narayana Memorial Scholarship Examination 2026.\n\nPresented in memory of Late Sri N. Surya Narayana Garu, a passionate teacher whose dedication to education continues to inspire young minds.',
    vision:
      'To encourage academic excellence among students while honoring the educational values and teaching legacy of Late Sri N. Surya Narayana Garu.',
    ctaLabel: 'Register Now',
    poweredByTagline: 'Connecting Students, Tutors and Opportunities.',
    prizeStructure: [
      { rank: 1, title: '1st Prize', amount: 3000, currency: 'INR' },
      { rank: 2, title: '2nd Prize', amount: 2000, currency: 'INR' },
      { rank: 3, title: '3rd Prize', amount: 1000, currency: 'INR' },
      { rank: 4, title: '4th Rank', amount: 500, currency: 'INR' },
      { rank: 5, title: '5th Rank', amount: 500, currency: 'INR' },
      { rank: 6, title: '6th Rank', amount: 500, currency: 'INR' },
      { rank: 7, title: '7th Rank', amount: 500, currency: 'INR' },
      { rank: 8, title: '8th Rank', amount: 500, currency: 'INR' },
      { rank: 9, title: '9th Rank', amount: 500, currency: 'INR' },
      { rank: 10, title: '10th Rank', amount: 500, currency: 'INR' },
    ],
    applicationStartDate: '2026-06-22T00:00',
    applicationEndDate: '2026-07-25T23:59',
    examStartDate: '2026-07-26T10:00',
    examEndDate: '2026-07-26T23:59',
    interviewStartDate: '',
    resultDate: '',
    duration: 30,
    totalMarks: 30,
    passingPercentage: 80,
    shuffleQuestions: true,
    shuffleOptions: true,
    status: 'OPEN',
  };
};

const defaultQuestionForm = {
  questionText: '',
  marks: '1',
  difficulty: 'MEDIUM' as 'EASY' | 'MEDIUM' | 'HARD',
  optionA: '',
  optionB: '',
  optionC: '',
  optionD: '',
  correctIndex: '0',
};

const formatDate = (value?: string) => {
  if (!value) return 'Not set';
  return new Date(value).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const getErrorMessage = (error: any, fallback: string) => {
  const message = error?.response?.data?.message;
  if (typeof message === 'string') return message;
  if (Array.isArray(message) && message[0]) return String(message[0]);
  return fallback;
};

const AdminScholarshipPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<ScholarshipCampaign[]>([]);
  const [participants, setParticipants] = useState<ScholarshipRegistration[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [campaignForm, setCampaignForm] = useState<ScholarshipCreatePayload>(defaultCampaignForm());
  const [questionForm, setQuestionForm] = useState(defaultQuestionForm);
  const [topN, setTopN] = useState('10');
  const [minPercentage, setMinPercentage] = useState('40');
  const [interviewDrafts, setInterviewDrafts] = useState<Record<string, { scheduledAt: string; meetingLink: string; interviewerName: string }>>({});
  const [loading, setLoading] = useState(true);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const selectedCampaign = useMemo(
    () => campaigns.find((campaign) => campaign.scholarshipId === selectedId) || campaigns[0],
    [campaigns, selectedId],
  );

  const participantStats = useMemo(() => {
    const submittedStatuses: ScholarshipRegistration['status'][] = [
      'EXAM_SUBMITTED',
      'SHORTLISTED',
      'INTERVIEW_SCHEDULED',
      'INTERVIEW_COMPLETED',
      'SELECTED',
      'NOT_SELECTED',
      'CERTIFICATE_READY',
    ];
    const shortlistedStatuses: ScholarshipRegistration['status'][] = [
      'SHORTLISTED',
      'INTERVIEW_SCHEDULED',
      'INTERVIEW_COMPLETED',
      'SELECTED',
      'CERTIFICATE_READY',
    ];

    return {
      registrations: participants.length,
      attemptsSubmitted: participants.filter((participant) => submittedStatuses.includes(participant.status)).length,
      shortlisted: participants.filter((participant) => shortlistedStatuses.includes(participant.status)).length,
      winners: participants.filter((participant) => participant.status === 'SELECTED' || Number(participant.finalRank || 0) > 0).length,
    };
  }, [participants]);

  const dashboardStats = useMemo(() => {
    const stats = selectedCampaign?.stats;
    return {
      registrations: Math.max(Number(stats?.registrations || 0), participantStats.registrations),
      attemptsSubmitted: Math.max(Number(stats?.attemptsSubmitted || 0), participantStats.attemptsSubmitted),
      shortlisted: Math.max(Number(stats?.shortlisted || 0), participantStats.shortlisted),
      winners: Math.max(Number(stats?.winners || 0), participantStats.winners),
    };
  }, [participantStats, selectedCampaign?.stats]);

  const loadOverview = async () => {
    const data = await scholarshipService.getAdminOverview();
    setCampaigns(Array.isArray(data) ? data : []);
    if (!selectedId && data?.[0]) setSelectedId(data[0].scholarshipId);
  };

  const loadParticipants = async (scholarshipId: string) => {
    if (!scholarshipId) return;
    setParticipantsLoading(true);
    try {
      const data = await scholarshipService.getParticipants(scholarshipId);
      setParticipants(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Failed to load scholarship participants', err);
      setParticipants([]);
      setError(getErrorMessage(err, 'Unable to load scholarship participants.'));
    } finally {
      setParticipantsLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    scholarshipService
      .getAdminOverview()
      .then((data) => {
        if (!mounted) return;
        setCampaigns(Array.isArray(data) ? data : []);
        if (data?.[0]) setSelectedId(data[0].scholarshipId);
      })
      .catch((err) => {
        console.error('Failed to load scholarship admin', err);
        if (mounted) setError('Unable to load scholarship admin data.');
      })
      .finally(() => {
      if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (selectedId) void loadParticipants(selectedId);
  }, [selectedId]);

  const refreshSelectedScholarship = async () => {
    try {
      setBusy(true);
      setError(null);
      setNotice(null);
      await loadOverview();
      if (selectedId) await loadParticipants(selectedId);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Unable to refresh scholarship data.'));
    } finally {
      setBusy(false);
    }
  };

  const runAction = async (action: () => Promise<void>, successMessage: string) => {
    try {
      setBusy(true);
      setError(null);
      setNotice(null);
      await action();
      setNotice(successMessage);
      await loadOverview();
      if (selectedId) await loadParticipants(selectedId);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Admin action failed.'));
    } finally {
      setBusy(false);
    }
  };

  const createCampaign = async (event: React.FormEvent) => {
    event.preventDefault();
    await runAction(async () => {
      const saved = await scholarshipService.createScholarship({
        ...campaignForm,
        interviewStartDate: campaignForm.interviewStartDate || undefined,
        resultDate: campaignForm.resultDate || undefined,
        duration: Number(campaignForm.duration || 30),
        totalMarks: Number(campaignForm.totalMarks || 30),
        passingPercentage: Number(campaignForm.passingPercentage || 80),
      });
      setSelectedId(saved.scholarshipId);
      setCampaignForm(defaultCampaignForm());
    }, 'Scholarship campaign created.');
  };

  const addQuestion = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedCampaign) return;
    const options = [
      questionForm.optionA,
      questionForm.optionB,
      questionForm.optionC,
      questionForm.optionD,
    ].filter((value) => value.trim());
    if (options.length < 2) {
      setError('Add at least two options.');
      return;
    }

    await runAction(async () => {
      await scholarshipService.addQuestion(selectedCampaign.scholarshipId, {
        questionText: questionForm.questionText,
        questionType: 'MCQ',
        marks: Number(questionForm.marks || 1),
        difficulty: questionForm.difficulty,
        options: options.map((text, index) => ({
          text,
          isCorrect: index === Number(questionForm.correctIndex || 0),
        })),
      });
      setQuestionForm(defaultQuestionForm);
    }, 'Question added.');
  };

  const scheduleInterview = async (registration: ScholarshipRegistration) => {
    if (!selectedCampaign) return;
    const draft = interviewDrafts[registration.registrationId];
    if (!draft?.scheduledAt) {
      setError('Select an interview date and time.');
      return;
    }
    await runAction(async () => {
      await scholarshipService.scheduleInterview({
        scholarshipId: selectedCampaign.scholarshipId,
        registrationId: registration.registrationId,
        scheduledAt: draft.scheduledAt,
        mode: 'ONLINE',
        meetingLink: draft.meetingLink,
        interviewerName: draft.interviewerName,
      });
    }, 'Interview scheduled.');
  };

  if (loading) {
    return <div className="rounded-lg border border-slate-200 bg-white p-6 text-slate-600">Loading scholarship admin...</div>;
  }

  return (
    <div className="space-y-6 text-slate-950">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-cyan-700">
              <Trophy className="h-4 w-4" />
              Scholarship Admin
            </span>
            <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950">
              Manage campaigns, exams, participants, interviews, and winners.
            </h1>
          </div>
          <select
            value={selectedId}
            onChange={(event) => setSelectedId(event.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
          >
            {campaigns.map((campaign) => (
              <option key={campaign.scholarshipId} value={campaign.scholarshipId}>
                {campaign.title}
              </option>
            ))}
          </select>
        </div>
      </section>

      {error && <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}
      {notice && <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{notice}</div>}

      {selectedCampaign && (
        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          {[
            { label: 'Status', value: selectedCampaign.status, icon: CheckCircle2 },
            { label: 'Registrations', value: dashboardStats.registrations, icon: Users },
            { label: 'Submitted', value: dashboardStats.attemptsSubmitted, icon: ClipboardList },
            { label: 'Shortlisted', value: dashboardStats.shortlisted, icon: Award },
            { label: 'Winners', value: dashboardStats.winners, icon: Trophy },
          ].map((metric) => {
            const Icon = metric.icon;
            return (
              <div key={metric.label} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <Icon className="h-5 w-5 text-cyan-700" />
                <p className="mt-3 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{metric.label}</p>
                <p className="mt-2 text-2xl font-semibold text-slate-950">{metric.value}</p>
              </div>
            );
          })}
        </section>
      )}

      <div className="grid gap-6 xl:grid-cols-[420px_minmax(0,1fr)]">
        <div className="space-y-6">
          <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-950">Create Campaign</h2>
            <form className="mt-5 space-y-4" onSubmit={createCampaign}>
              <label className="block text-sm font-medium text-slate-700">
                Title
                <input
                  value={campaignForm.title}
                  onChange={(event) => setCampaignForm((prev) => ({ ...prev, title: event.target.value }))}
                  required
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                />
              </label>
              <label className="block text-sm font-medium text-slate-700">
                Description
                <textarea
                  value={campaignForm.description}
                  onChange={(event) => setCampaignForm((prev) => ({ ...prev, description: event.target.value }))}
                  rows={3}
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                />
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-sm font-medium text-slate-700">
                  Application start
                  <input
                    type="datetime-local"
                    value={campaignForm.applicationStartDate}
                    onChange={(event) => setCampaignForm((prev) => ({ ...prev, applicationStartDate: event.target.value }))}
                    required
                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                  />
                </label>
                <label className="block text-sm font-medium text-slate-700">
                  Application end
                  <input
                    type="datetime-local"
                    value={campaignForm.applicationEndDate}
                    onChange={(event) => setCampaignForm((prev) => ({ ...prev, applicationEndDate: event.target.value }))}
                    required
                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                  />
                </label>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-sm font-medium text-slate-700">
                  Exam start
                  <input
                    type="datetime-local"
                    value={campaignForm.examStartDate}
                    onChange={(event) => setCampaignForm((prev) => ({ ...prev, examStartDate: event.target.value }))}
                    required
                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                  />
                </label>
                <label className="block text-sm font-medium text-slate-700">
                  Exam end
                  <input
                    type="datetime-local"
                    value={campaignForm.examEndDate}
                    onChange={(event) => setCampaignForm((prev) => ({ ...prev, examEndDate: event.target.value }))}
                    required
                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                  />
                </label>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <label className="block text-sm font-medium text-slate-700">
                  Minutes
                  <input
                    type="number"
                    min={5}
                    value={campaignForm.duration}
                    onChange={(event) => setCampaignForm((prev) => ({ ...prev, duration: Number(event.target.value) }))}
                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                  />
                </label>
                <label className="block text-sm font-medium text-slate-700">
                  Marks
                  <input
                    type="number"
                    min={1}
                    value={campaignForm.totalMarks}
                    onChange={(event) => setCampaignForm((prev) => ({ ...prev, totalMarks: Number(event.target.value) }))}
                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                  />
                </label>
                <label className="block text-sm font-medium text-slate-700">
                  Pass %
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={campaignForm.passingPercentage}
                    onChange={(event) => setCampaignForm((prev) => ({ ...prev, passingPercentage: Number(event.target.value) }))}
                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                  />
                </label>
              </div>
              <button
                type="submit"
                disabled={busy}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 py-3 text-sm font-semibold text-white hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Plus className="h-4 w-4" />
                Create campaign
              </button>
            </form>
          </section>

          <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-950">Question Bank</h2>
            <form className="mt-5 space-y-4" onSubmit={addQuestion}>
              <label className="block text-sm font-medium text-slate-700">
                Question
                <textarea
                  value={questionForm.questionText}
                  onChange={(event) => setQuestionForm((prev) => ({ ...prev, questionText: event.target.value }))}
                  required
                  rows={3}
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                />
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                {[0, 1, 2, 3].map((index) => {
                  const key = `option${String.fromCharCode(65 + index)}` as 'optionA' | 'optionB' | 'optionC' | 'optionD';
                  return (
                    <label key={key} className="block text-sm font-medium text-slate-700">
                      Option {String.fromCharCode(65 + index)}
                      <input
                        value={questionForm[key]}
                        onChange={(event) => setQuestionForm((prev) => ({ ...prev, [key]: event.target.value }))}
                        className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                      />
                    </label>
                  );
                })}
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <label className="block text-sm font-medium text-slate-700">
                  Correct
                  <select
                    value={questionForm.correctIndex}
                    onChange={(event) => setQuestionForm((prev) => ({ ...prev, correctIndex: event.target.value }))}
                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                  >
                    <option value="0">A</option>
                    <option value="1">B</option>
                    <option value="2">C</option>
                    <option value="3">D</option>
                  </select>
                </label>
                <label className="block text-sm font-medium text-slate-700">
                  Marks
                  <input
                    type="number"
                    min={0.5}
                    step={0.5}
                    value={questionForm.marks}
                    onChange={(event) => setQuestionForm((prev) => ({ ...prev, marks: event.target.value }))}
                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                  />
                </label>
                <label className="block text-sm font-medium text-slate-700">
                  Difficulty
                  <select
                    value={questionForm.difficulty}
                    onChange={(event) => setQuestionForm((prev) => ({ ...prev, difficulty: event.target.value as any }))}
                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                  >
                    <option value="EASY">Easy</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HARD">Hard</option>
                  </select>
                </label>
              </div>
              <button
                type="submit"
                disabled={busy || !selectedCampaign}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 py-3 text-sm font-semibold text-white hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Plus className="h-4 w-4" />
                Add question
              </button>
            </form>
          </section>
        </div>

        <div className="space-y-6">
          {selectedCampaign && (
            <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-slate-950">{selectedCampaign.title}</h2>
                  <p className="mt-2 text-sm text-slate-600">
                    Registration: {formatDate(selectedCampaign.applicationStartDate)} to {formatDate(selectedCampaign.applicationEndDate)}
                  </p>
                  <p className="mt-1 text-sm text-slate-600">
                    Exam: {formatDate(selectedCampaign.examStartDate)} to {formatDate(selectedCampaign.examEndDate)}
                  </p>
                </div>
                <div className="grid gap-2 sm:grid-cols-3">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      void runAction(
                        () => scholarshipService.shortlist(selectedCampaign.scholarshipId, {
                          topN: Number(topN || 10),
                          minPercentage: Number(minPercentage || 0),
                        }).then(() => undefined),
                        'Candidates shortlisted.',
                      )
                    }
                    className="rounded-lg bg-cyan-700 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-800 disabled:opacity-60"
                  >
                    Shortlist
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      void runAction(
                        () => scholarshipService.declareWinners(selectedCampaign.scholarshipId).then(() => undefined),
                        'Winners declared.',
                      )
                    }
                    className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
                  >
                    Declare winners
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      void runAction(
                        () => scholarshipService.generateCertificates(selectedCampaign.scholarshipId).then(() => undefined),
                        'Certificates generated.',
                      )
                    }
                    className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-60"
                  >
                    Certificates
                  </button>
                </div>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <label className="text-sm font-medium text-slate-700">
                  Top N
                  <input
                    type="number"
                    min={1}
                    value={topN}
                    onChange={(event) => setTopN(event.target.value)}
                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                  />
                </label>
                <label className="text-sm font-medium text-slate-700">
                  Minimum percentage
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={minPercentage}
                    onChange={(event) => setMinPercentage(event.target.value)}
                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                  />
                </label>
              </div>
            </section>
          )}

          <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-950">Participants</h2>
                <p className="mt-1 text-sm text-slate-500">{participants.length} registration records loaded</p>
              </div>
              <button
                type="button"
                disabled={busy || participantsLoading || !selectedCampaign}
                onClick={() => void refreshSelectedScholarship()}
                className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {participantsLoading ? 'Refreshing...' : 'Refresh registrations'}
              </button>
            </div>

            {participantsLoading ? (
              <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 px-5 py-8 text-sm text-slate-500">
                Loading participants...
              </div>
            ) : participants.length === 0 ? (
              <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 px-5 py-8 text-sm text-slate-500">
                No participants registered yet.
              </div>
            ) : (
              <div className="mt-5 space-y-4">
                {participants.map((participant) => {
                  const draft = interviewDrafts[participant.registrationId] || {
                    scheduledAt: '',
                    meetingLink: '',
                    interviewerName: '',
                  };
                  return (
                    <article key={participant.registrationId} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                              {participant.status}
                            </span>
                            {participant.rank && (
                              <span className="rounded-full bg-cyan-100 px-3 py-1 text-xs font-semibold text-cyan-800">
                                Rank {participant.rank}
                              </span>
                            )}
                            {participant.finalRank && (
                              <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
                                Winner {participant.finalRank}
                              </span>
                            )}
                          </div>
                          <h3 className="mt-3 text-lg font-semibold text-slate-950">{participant.studentName}</h3>
                          <p className="mt-1 text-sm text-slate-600">
                            {participant.classLevel} | {participant.schoolName || 'School not listed'}
                          </p>
                          <p className="mt-2 text-sm text-slate-700">
                            Hall ticket {participant.registrationId}
                            {participant.createdAt ? ` | Registered ${formatDate(participant.createdAt)}` : ''}
                          </p>
                          <p className="mt-2 text-sm text-slate-700">
                            Score {participant.score || 0} | {participant.percentageScore || 0}%
                          </p>
                          <p className="mt-1 text-xs text-slate-500">
                            {participant.studentEmail || 'No email'} | {participant.studentPhone || 'No phone'}
                            {participant.city || participant.state ? ` | ${[participant.city, participant.state].filter(Boolean).join(', ')}` : ''}
                          </p>
                        </div>

                        <div className="rounded-lg border border-slate-200 bg-white p-3">
                          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                            <CalendarPlus className="h-4 w-4 text-cyan-700" />
                            Interview
                          </div>
                          <input
                            type="datetime-local"
                            value={draft.scheduledAt}
                            onChange={(event) =>
                              setInterviewDrafts((prev) => ({
                                ...prev,
                                [participant.registrationId]: { ...draft, scheduledAt: event.target.value },
                              }))
                            }
                            className="mt-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                          />
                          <input
                            value={draft.meetingLink}
                            onChange={(event) =>
                              setInterviewDrafts((prev) => ({
                                ...prev,
                                [participant.registrationId]: { ...draft, meetingLink: event.target.value },
                              }))
                            }
                            placeholder="Meeting link"
                            className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                          />
                          <input
                            value={draft.interviewerName}
                            onChange={(event) =>
                              setInterviewDrafts((prev) => ({
                                ...prev,
                                [participant.registrationId]: { ...draft, interviewerName: event.target.value },
                              }))
                            }
                            placeholder="Interviewer"
                            className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                          />
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void scheduleInterview(participant)}
                            className="mt-3 w-full rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-2 text-sm font-semibold text-cyan-800 hover:bg-cyan-100 disabled:opacity-60"
                          >
                            Schedule
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default AdminScholarshipPage;

